import Cancel from '@mui/icons-material/Cancel'
import CheckCircle from '@mui/icons-material/CheckCircle'
import { CircularProgress, Grid } from '@mui/material'
import debounce from 'lodash/debounce'
import { Suspense, useCallback, useContext, useEffect, useRef, useState } from 'react'
// import 'react-phone-input-2/lib/bootstrap.css'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)
import { StyledPhoneInputContainer } from 'src/clinic/views/onboardng/ClinicOnboardingStyles'
import {
  CustomInput,
  CustomSelect,
  CustomTextArea,
  selectTypes,
} from 'src/consumer/components/helperComponents'
import {
  CustomAsyncCreatablePaginate,
  ProfilePictureButtonComponent,
} from 'src/consumer/components/helperComponents/dynamic'
import { StyledLabel } from 'src/consumer/components/helperComponentsStyles'
import { timezones_list } from 'src/consumer/constants'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { UnsecureSetupContext } from 'src/consumer/context/UnsecureSetupContext'
import { isOnboardedAsDoctor, isOnboardedAsTherapist } from 'src/mentor/layout/utils'
import { getMPData } from 'src/mentor/services'
import { getUserSignInId } from 'src/shared/utils/accountIdentity'
import { checkIfSlugValid } from '../../MentorCalendarPage/MentorCalendarComponents'
import { loadCities } from '../../MentorPublicPage/MentorPublicPage'
import {
  ProfileComponentsContainer,
  SectionHeading,
  SectionSubHeading,
  StrongHeading,
  StyledMentorPhoneNumberInput,
  StyledSlugInput,
  StyledSlugInputBox,
  StyledSlugPrefix,
} from '../MentorProfilePageStyles'
import {
  convertToEncodedOffset,
  decodeEncodedOffset,
  getCustomShortBioForMentor,
  getSuggestedSlugForCreator,
} from '../utils'

function sortByAlphabet(item1, item2, field) {
  if (item1?.[field] < item2?.[field]) {
    return -1
  }
  if (item1?.[field] > item2?.[field]) {
    return 1
  }
  return 0
}

export function BasicDetails({
  formState,
  setFormState,
  setButtonValidation,
  handleChange,
  handleNestedChange,
  profileData: mentorProfileData,
  slugInitialUrl,
  dropDownContextData,
}) {
  const { unSecureSetupData } = useContext(UnsecureSetupContext)
  const profileContext = useContext(ProfileContext)
  const profileData = profileContext?.profileData
  const signInId = getUserSignInId(profileData || mentorProfileData)

  const timeZone = formState?.timeZone?.offset
    ? { offset: decodeEncodedOffset(formState?.timeZone?.offset), name: formState?.timeZone?.title }
    : null
  const [verifyClick, setVerifyClick] = useState(false)

  const [citiesDropDownLoading, setCitiesDropDownLoading] = useState(true)

  const [slugValidationText, setSlugValidationText] = useState('Your Slug is invalid.')
  const [slugValidationLoading, setSlugValidationLoading] = useState(false)
  const [slugValidation, setSlugValidation] = useState()

  const [metaCategories, setMetaCategories] = useState()
  const [metaCategoriesLoading, setMetaCategoriesLoading] = useState(true)

  const domainOptions = dropDownContextData?.domains
    ?.filter((tag) => tag?.type === 'domain')
    ?.sort((tag1, tag2) => sortByAlphabet(tag1, tag2, 'name'))

  const categoryOptions = metaCategories
    ?.filter((cat) => cat?.categoryType === 'service-provider')
    ?.sort((cat1, cat2) => sortByAlphabet(cat1, cat2, 'name'))

  const therapistOptions = metaCategories
    ?.filter((cat) => {
      return cat?.id === 'therapist'
    })
    ?.sort((cat1, cat2) => sortByAlphabet(cat1, cat2, 'name'))

  const doctorSpecializations = metaCategories
    ?.filter((cat) => cat?.categoryType === 'doctor')
    ?.sort((cat1, cat2) => sortByAlphabet(cat1, cat2, 'name'))

  const therapistSpecializations = metaCategories
    ?.filter((cat) => cat?.categoryType === 'therapist')
    ?.sort((cat1, cat2) => sortByAlphabet(cat1, cat2, 'name'))

  const slugInvalid = slugValidation?.unavailable || !slugValidation?.valid
  const showTempSlug = !formState?.slug
  const slugRef = useRef(null)

  const hasDoctorCategory = formState?.categories?.includes('doctor')
  const hasTherapistCategory = formState?.categories?.includes('therapist')

  let specializationsDropdownOptions = []

  if (hasDoctorCategory) {
    specializationsDropdownOptions = [
      ...specializationsDropdownOptions,
      ...(doctorSpecializations || []),
    ]
  }
  if (hasTherapistCategory) {
    specializationsDropdownOptions = [
      ...specializationsDropdownOptions,
      ...(therapistSpecializations || []),
    ]
  }

  // const shortBioInputRef = useRef(null)
  const showTempShortBio = !formState?.shortDesc

  async function validateSlug(currentSlug) {
    const validation = await checkIfSlugValid(currentSlug, mentorProfileData)
    if (validation?.unavailable) {
      setSlugValidationText('This URL is already taken.')
    } else if (!validation?.valid) {
      setSlugValidationText('This URL is in the incorrect format.')
    }
    setSlugValidation(validation)
    setButtonValidation(!(validation?.unavailable || !validation?.valid))
    setSlugValidationLoading(false)
  }

  const debSlugValidation = useCallback(
    debounce(async (slug) => {
      await validateSlug(slug)
    }, 500),
    [mentorProfileData],
  )

  function handleSlug(e) {
    let event = e
    // if (!formState?.slug) {
    //   event.target.value = tempSlug
    // }

    handleChange(event)
    setButtonValidation(false)
    setSlugValidationLoading(true)
    debSlugValidation(event.target.value)
  }

  function handlePersonalizeChange(event) {
    handleNestedChange(event, 'personalize')
  }

  async function getCategories() {
    const resp = await getMPData('/api/secure/teacher/meta-categories')
    if (resp?.status === 200) {
      if (resp?.data) {
        const filteredMetaCategories = resp.data?.filter((item) => {
          if (item?.hasOwnProperty('appListable')) {
            if (item?.appListable === true) {
              return true
            } else {
              return false
            }
          } else {
            return true
          }
        })
        setMetaCategories(filteredMetaCategories)
      }
    }
    setMetaCategoriesLoading(false)
  }

  function handleChangeLocation(event) {
    handleNestedChange(event, 'location')
  }

  function changeCountry(selectValue) {
    setFormState((prev) => ({
      ...prev,
      location: { ...prev?.location, countryCode: selectValue?.id, country: selectValue?.name },
    }))
  }
  function handleChangeTimeZone(selectValue) {
    setFormState((prev) => ({
      ...prev,
      timeZone: { offset: convertToEncodedOffset(selectValue?.gmtvalue), title: selectValue?.name },
    }))
  }

  function handleSlugClick() {
    slugRef?.current?.focus()
  }

  function slugFocusHandling() {
    if (!formState?.slug && !!formState?.tempSlug) {
      setFormState((prev) => ({
        ...prev,
        slug: prev?.tempSlug || '',
      }))
    }
  }

  function shortBioFocusHandling() {
    if (!formState?.shortDesc && !!formState?.tempShortDesc) {
      setFormState((prev) => ({
        ...prev,
        shortDesc: prev?.tempShortDesc || '',
      }))
    }
  }

  function handleChangePhone(phone) {
    const phoneToSet = '+' + phone
    handleChange({ target: { id: 'mobile', value: phoneToSet } })
  }

  function handleChangeWhatsappId(phone) {
    const phoneToSet = phone
    setFormState((prev) => ({
      ...prev,
      communicationPref: {
        ...prev?.communicationPref,
        whatsapp: { ...prev?.communicationPref?.whatsapp, uid: phoneToSet, enabled: true },
      },
    }))
  }

  async function getSuggestedSlug({ formData }) {
    setSlugValidationLoading(true)
    const suggestedSlug = await getSuggestedSlugForCreator({ formState: formData })
    setFormState((prev) => ({
      ...prev,
      tempSlug: suggestedSlug,
    }))
    if (!!suggestedSlug) {
      await debSlugValidation(suggestedSlug)
    }

    setSlugValidationLoading(false)
  }

  function getSuggestedShortBio({ formData, metaCats }) {
    const suggestedShortBio = getCustomShortBioForMentor({
      formState: formData,
      metaCategories: metaCats,
    })
    // const suggestedShortBio = "getSuggestedShortBio({ formState: formData })"
    setFormState((prev) => ({
      ...prev,
      tempShortDesc: suggestedShortBio,
    }))
  }

  const debSlugSuggest = useCallback(
    debounce(async ({ formData }) => {
      await getSuggestedSlug({ formData })
    }, 500),
    [mentorProfileData],
  )

  useEffect(() => {
    if (mentorProfileData && formState?.slug && !slugValidation) {
      validateSlug(formState?.slug)
    }
  }, [mentorProfileData, formState?.slug])

  useEffect(() => {
    if (!timeZone) {
      const guessedTimeZoneString = Intl.DateTimeFormat().resolvedOptions().timeZone
      const guessedTimeZone = timezones_list.find((tz) => tz?.timezone === guessedTimeZoneString)

      console.log('guessedTimeZone', guessedTimeZone)
      console.log('guessedTimeZoneString', guessedTimeZoneString)
      handleChangeTimeZone(guessedTimeZone)
    }
  }, [formState])

  useEffect(() => {
    if (!formState?.slug && formState?.firstName) {
      debSlugSuggest({ formData: formState })
    }
  }, [formState?.firstName])

  useEffect(() => {
    if (!formState?.shortDesc) {
      getSuggestedShortBio({ formData: formState, metaCats: metaCategories })
    }
  }, [
    formState?.firstName,
    formState?.specializedCategories,
    formState?.location,
    formState?.categories,
    metaCategories,
  ])

  useEffect(() => {
    getCategories()
  }, [])

  useEffect(() => {
    console.log('formState', formState)
  }, [formState])

  return (
    <ProfileComponentsContainer container spacing={4}>
      <Grid item xs={12}>
        <div className="d-flex justify-content-between align-items-center pr-2">
          <div>
            <StrongHeading>Your Profile Photo</StrongHeading>
            <SectionSubHeading>
              This is the basic information we need for your profile.
            </SectionSubHeading>
          </div>
          <Suspense fallback={<CircularProgress size={40} />}>
            <ProfilePictureButtonComponent
              formState={formState}
              setFormState={setFormState}
              handleChange={handleChange}
            />
          </Suspense>
        </div>
      </Grid>
      <Grid item paddingBottom={'25px'} xs={12}>
        <SectionHeading>Profile Details</SectionHeading>
        <SectionSubHeading>
          This is the basic information we need for your profile.
        </SectionSubHeading>
      </Grid>
      <Grid item xs={12}>
        <CustomInput
          id="firstName"
          label={'Full Name'}
          value={formState?.firstName || ''}
          onChange={handleChange}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <CustomInput
          label="Sign-in ID"
          value={signInId || ''}
          disabled
        />
      </Grid>
      <Grid item xs={12} md={6}>
        {/* <PhoneOTPComponent
          phoneValue={formState?.mobile}
          setPhoneValue={handleChangePhone}
          phoneValid={}
          setValidity={() => {}}
          userProfileData={profileData}
          layout="grid"
        /> */}
        <CustomInput
          label="Email"
          value={formState?.email || ''}
          country="in"
          disabled
          // invalid={!profileData?.mobileVerified}
        />
        {/* {!profileData?.mobileVerified && (
          <StyledMentorJoinVerifyLink
            variant={'text'}
            onClick={() => {
              setVerifyClick(true)
            }}
          >
            Click here to verify your phone number and continue
          </StyledMentorJoinVerifyLink>
        )} */}
      </Grid>
      <Grid item xs={12} md={6}>
        <CustomInput
          label="Phone"
          inputAndMessageContainer={StyledPhoneInputContainer}
          component={StyledMentorPhoneNumberInput}
          value={formState?.mobile || ''}
          country="in"
          showDropDown={false}
          onChange={handleChangePhone}
          // invalid={!profileData?.mobileVerified}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <CustomInput
          label="Whatsapp Phone Number"
          inputAndMessageContainer={StyledPhoneInputContainer}
          component={StyledMentorPhoneNumberInput}
          value={formState?.communicationPref?.whatsapp?.uid || ''}
          country="in"
          showDropDown={false}
          onChange={handleChangeWhatsappId}
          // invalid={!profileData?.mobileVerified}
        />
      </Grid>
      {!!slugInitialUrl && (
        <Grid display="flex" flexDirection="column" gap="0.5rem" item xs={12}>
          <StrongHeading>Create your unique AUMHUM URL*</StrongHeading>
          <StyledLabel>
            Choose a URL that describes you or your business in a concise way. Make it short and
            easy to remember so you can share links with ease. Start typing in the box below.
          </StyledLabel>
          <StyledLabel>
            Your identifier should be lowercase alphabetic (a-z) and may include hyphens(-). It
            should not contain any special characters or spaces.
          </StyledLabel>
          <StyledSlugInputBox onClick={handleSlugClick}>
            <StyledSlugPrefix>{slugInitialUrl}</StyledSlugPrefix>
            <StyledSlugInput
              id={showTempSlug ? 'tempSlug' : 'slug'}
              ref={slugRef}
              onFocus={slugFocusHandling}
              label={'Unique Username'}
              value={!showTempSlug ? (formState?.slug || '') : (formState?.tempSlug || '')}
              onChange={handleSlug}
              required
            />
            {slugValidationLoading && (
              <CircularProgress size={30} sx={{ height: '19.5px !important', color: '#4d4d4d' }} />
            )}
            {!slugInvalid && !slugValidationLoading && <CheckCircle sx={{ color: 'green' }} />}
            {slugInvalid && !slugValidationLoading && <Cancel sx={{ color: 'red' }} />}
          </StyledSlugInputBox>
          {slugInvalid && <span>{slugValidationText}</span>}
        </Grid>
      )}
      <Grid id="service-info-heading" item xs={12}>
        <SectionHeading>Service Information</SectionHeading>
        {/* <SectionSubHeading>
          These are the services
        </SectionSubHeading> */}
      </Grid>
      {!isOnboardedAsDoctor() && (
        <Grid item xs={12}>
          <CustomSelect
            isMulti
            id="categories"
            label={
              <strong>
                {isOnboardedAsDoctor() || isOnboardedAsTherapist() ? 'Categories' : 'Services'}
              </strong>
            }
            subLabel={
              isOnboardedAsDoctor() || isOnboardedAsTherapist()
                ? 'Select the most appropriate that apply'
                : 'These are the services that you can provide in AUMHUM.'
            }
            value={formState?.categories}
            onChangeEvent={handleChange}
            isLoading={metaCategoriesLoading}
            options={isOnboardedAsTherapist() ? therapistOptions : categoryOptions}
            getOptionValue={(option) => option?.id}
            getOptionLabel={(option) => option.name}
            selectType={selectTypes.string}
            required
          />
        </Grid>
      )}
      {(hasDoctorCategory || hasTherapistCategory) && (
        <Grid item xs={12}>
          <CustomSelect
            isMulti
            id="specializedCategories"
            label={<strong>Specializations</strong>}
            // subLabel="These are the areas/issues you are equipped to help with."
            value={formState?.specializedCategories}
            onChangeEvent={handleChange}
            // loadOptions={loadFocusAreas}
            options={specializationsDropdownOptions}
            getOptionValue={(option) => option?.id}
            getOptionLabel={(option) => option.name || option?.title}
            selectType={selectTypes.string}
          />
        </Grid>
      )}
      {(formState?.categories?.includes('therapist') ||
        formState?.categories?.includes('counsellor') ||
        formState?.categories?.includes('psychiatrist') ||
        formState?.categories?.includes('clinical_psychologist') ||
        formState?.categories?.includes('counselling_psychologist') ||
        formState?.categories?.includes('"mental-health-expert"')) && (
        <Grid item xs={12}>
          {/* <CustomAsyncPaginate
            isMulti
            id="focusArea"
            label={<strong>Focus Areas</strong>}
            subLabel="Choose the focus areas of your content."
            value={formState?.personalize?.focusArea}
            onChangeEvent={handlePersonalizeChange}
            loadOptions={loadFocusAreas}
            // options={allTags}
            getOptionValue={(option) => option?.id}
            getOptionLabel={(option) => option.name}
            debounceTimeout={500}
            additional={{
              page: 1,
            }}
          /> */}
          <CustomSelect
            isMulti
            id="focusAreas"
            label={<strong>Focus Areas</strong>}
            subLabel="These are the areas/issues you are equipped to help with."
            value={formState?.personalize?.focusAreas}
            onChangeEvent={handlePersonalizeChange}
            // loadOptions={loadFocusAreas}
            options={domainOptions
              ?.map((tag) => ({ ...tag, name: tag?.name || tag?.tag }))
              ?.sort((tag1, tag2) => tag1.name.localeCompare(tag2.name))}
            getOptionValue={(option) => option?.id}
            getOptionLabel={(option) => option.name || option?.tag}
            selectType={selectTypes.string}
            // debounceTimeout={500}
            // additional={{
            //   page: 1,
            // }}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <div>
          <StrongHeading>Location</StrongHeading>
          <SectionSubHeading>
            Enter your city or country. Your location helps us match you with your community.
          </SectionSubHeading>
        </div>
      </Grid>
      <Grid item xs={12} md={12}>
        <CustomTextArea
          id="address"
          label={'Address'}
          minHeight={80}
          value={formState?.location?.address || ''}
          onChange={handleChangeLocation}
          options={unSecureSetupData?.metaCountries}
          getOptionValue={(option) => option?.id}
          getOptionLabel={(option) => option.name}
          selectType={selectTypes.string}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <CustomSelect
          id="countryCode"
          label={'Country'}
          value={formState?.location?.countryCode}
          onChange={changeCountry}
          options={unSecureSetupData?.metaCountries}
          getOptionValue={(option) => option?.id}
          getOptionLabel={(option) => option.name}
          selectType={selectTypes.string}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        {/* <CustomInput
          id="city"
          label={'City'}
          value={formState?.location?.city}
          placeholder="Enter your city..."
          onChange={handleChangeLocation}
          required
        /> */}

        <Suspense fallback={<CircularProgress size={20} />}>
          <CustomAsyncCreatablePaginate
            id="city"
            label="City"
            cacheUniqs={[formState?.location?.countryCode]}
            value={
              formState?.location?.city
                ? {
                    id: formState?.location?.city,
                    title: formState?.location?.city,
                  }
                : null
            }
            loadOptions={loadCities}
            isClearable
            onChange={(val) => {
              setFormState((prev) => {
                return {
                  ...prev,
                  location: {
                    ...prev.location,
                    city: val?.title,
                  },
                }
            })
          }}
          onCreateOption={(cOptn) => {
            setFormState((prev) => {
              return {
                ...prev,
                location: {
                  ...prev.location,
                  city: cOptn,
                },
              }
            })
          }}
          getOptionValue={(option) => option?.id}
          getOptionLabel={(option) => option.title}
          // inputvalue={qualificationInput}
          // onInputChange={handleInputChange}
          placeholder="Select city..."
          debounceTimeout={500}
          getNewOptionData={(inputValue, optionLabel) => ({
            id: inputValue,
            title: optionLabel,
          })}
          formatCreateLabel={(value) => `Press enter to select: ${value}`}
          selectType={selectTypes.string}
          noOptionsMessage={() => 'Enter your city.'}
          additional={{
            page: 0,
            size: 100,
            countryCode: formState?.location?.countryCode,
          }}
          styles={{
            option: (provided, state) => {
              if (state.data.__isNew__) {
                return { ...provided, color: '#6b50aa' }
              } else {
                return provided
              }
            },
          }}
        />
        </Suspense>
      </Grid>
      {/* <Grid item xs={12} md={6}>
        <CustomInput
          id="lat"
          label={'Latitude'}
          value={formState?.location?.lat}
          placeholder="Enter Latitude..."
          type="number"
          onChange={handleChangeLocation}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <CustomInput
          id="lng"
          label={'Longitude'}
          value={formState?.location?.lng}
          placeholder="Enter Longitude..."
          type="number"
          step="0.01"
          onChange={handleChangeLocation}
        />
      </Grid> */}
      <Grid item xs={12} md={6}>
        <CustomInput
          id="pincode"
          label={'Pincode'}
          value={formState?.location?.pincode || ''}
          placeholder="Enter your pincode..."
          type="number"
          step="0.0001"
          onChange={(e) => {
            const inputValue = e.target.value.slice(0, 6) // Limit input to 6 characters
            setFormState((prev) => ({
              ...prev,
              location: {
                ...prev.location,
                pincode: inputValue,
              },
            }))
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <CustomSelect
          id="timeZone"
          label="Time Zone"
          value={timeZone}
          onChange={handleChangeTimeZone}
          options={timezones_list}
          getOptionValue={(option) => option?.gmtvalue}
          getOptionLabel={(option) => option?.name}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <CustomSelect
          isMulti
          id="languages"
          label={'Languages'}
          subLabel={'Let users know what languages you speak.'}
          value={formState?.languages}
          onChangeEvent={handleChange}
          options={unSecureSetupData?.appLanguages}
          getOptionValue={(option) => option?.id}
          getOptionLabel={(option) => option.language}
          selectType={selectTypes?.string}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <CustomTextArea
          id={showTempShortBio ? 'tempShortDesc' : 'shortDesc'}
          label={'Short Bio'}
          minHeight={80}
          value={showTempShortBio ? (formState?.tempShortDesc || '') : (formState?.shortDesc || '')}
          onChange={handleChange}
          onFocus={shortBioFocusHandling}
          required
        />
      </Grid>
      {/* <Grid item xs={12}>
       <CustomTextArea
         id="longDesc"
         label={'Long Bio'}
         value={formState?.LongDesc}
         onChange={handleChange}
         required
       />
      </Grid> */}
      {/* <Grid item xs={12}>
        <CustomInput
          id="website"
          label={'Website'}
          value={formState?.website}
          onChange={handleChange}
          placeholder="Enter your website..."
        />
      </Grid> */}
    </ProfileComponentsContainer>
  )
}
